import { Routes } from '@angular/router';

import { environment } from '../environments/environment';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard, superadminGuard } from './core/guards/role.guard';

/** Password sign-up and reset exist only while the API still runs legacy auth; deleted at cutover. */
const legacyAuthRoutes: Routes = environment.legacyAuth
  ? [
      {
        path: 'register',
        loadComponent: () => import('./features/register/register.component').then((m) => m.RegisterComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./features/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./features/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
      },
    ]
  : [];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./shared/public-layout/public-layout.component').then((m) => m.PublicLayoutComponent),
    children: [
      // Public landing page, with the orange top bar from this layout
      { path: '', pathMatch: 'full', loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent) },
      { path: 'home', pathMatch: 'full', redirectTo: '' },
      // Identity-v2 sends the browser back to the `redirect` it was given, so no callback route is needed
      { path: 'login', loadComponent: () => import('./features/login/login.component').then((m) => m.LoginComponent) },
      ...legacyAuthRoutes,
    ],
  },

  // Public badge pages, styled like the mobile app (light/dark per device). Badge and Info show the pill switch;
  // confirm, submit and preview hide it.
  {
    path: 'badge/:uuid',
    loadComponent: () => import('./shared/badge-app-layout/badge-app-layout.component').then((m) => m.BadgeAppLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./features/badge/badge-display.component').then((m) => m.BadgeDisplayComponent),
      },
      {
        path: 'info',
        loadComponent: () => import('./features/badge/badge-info.component').then((m) => m.BadgeInfoComponent),
      },
      {
        path: 'confirm',
        // The badge isn't confirmed yet, so no Badge / Info switch here
        data: { hideBadgeNav: true },
        loadComponent: () => import('./features/badge/badge-confirm.component').then((m) => m.BadgeConfirmComponent),
      },
      {
        path: 'submit',
        data: { hideBadgeNav: true },
        loadComponent: () => import('./features/badge/badge-submit.component').then((m) => m.BadgeSubmitComponent),
      },
      {
        path: 'preview',
        data: { hideBadgeNav: true },
        loadComponent: () => import('./features/badge/badge-preview.component').then((m) => m.BadgePreviewComponent),
      },
    ],
  },

  // Everything in the shell needs a loaded Pass account (authGuard); roleGuard / superadminGuard then apply the
  // Pass role ladder from /auth/me.
  {
    path: '',
    loadComponent: () => import('./shared/shell/shell.component').then((m) => m.ShellComponent),
    children: [
      {
        path: 'dashboard',
        canActivate: [authGuard, roleGuard('viewer')],
        data: { wide: true },
        loadComponent: () => import('./dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'profiles',
        canActivate: [authGuard, roleGuard('viewer')],
        data: { wide: true },
        loadComponent: () => import('./profiles/profiles.component').then((m) => m.ProfilesComponent),
      },
      {
        path: 'photo-review',
        canActivate: [authGuard, roleGuard('viewer')],
        data: { wide: true },
        loadComponent: () => import('./photo-review/photo-review.component').then((m) => m.PhotoReviewComponent),
      },
      {
        path: 'profiles/new',
        canActivate: [authGuard, roleGuard('editor')],
        loadComponent: () => import('./features/profiles/profile-create.component').then((m) => m.ProfileCreateComponent),
      },
      {
        path: 'profiles/:id',
        canActivate: [authGuard, roleGuard('editor')],
        loadComponent: () => import('./features/profiles/profile-review.component').then((m) => m.ProfileReviewComponent),
      },
      {
        path: 'profiles/:id/photo',
        canActivate: [authGuard, roleGuard('editor')],
        loadComponent: () => import('./features/profiles/photo-override.component').then((m) => m.PhotoOverrideComponent),
      },
      {
        path: 'my-profile',
        canActivate: [authGuard],
        data: { wide: true },
        loadComponent: () => import('./my-profile/my-profile.component').then((m) => m.MyProfileComponent),
      },
      {
        path: 'support',
        canActivate: [authGuard],
        loadComponent: () => import('./features/support/support.component').then((m) => m.SupportComponent),
      },
      {
        path: 'audit-log',
        canActivate: [authGuard, roleGuard('admin')],
        data: { wide: true },
        loadComponent: () => import('./audit-log/audit-log.component').then((m) => m.AuditLogComponent),
      },
      {
        path: 'roster-sync',
        // Sample data only for now (see roster-sync.sample-data.ts)
        canActivate: [authGuard, roleGuard('admin')],
        data: { wide: true },
        loadComponent: () => import('./roster-sync/roster-sync.component').then((m) => m.RosterSyncComponent),
      },
      {
        path: 'settings',
        canActivate: [authGuard, roleGuard('admin')],
        data: { wide: true },
        loadComponent: () => import('./settings/settings.component').then((m) => m.SettingsComponent),
      },
      // Integrations moved under Settings; keep old links working
      { path: 'integrations', pathMatch: 'full', redirectTo: 'settings' },
      {
        path: 'users',
        canActivate: [authGuard, roleGuard('admin')],
        data: { wide: true },
        loadComponent: () => import('./users/users.component').then((m) => m.UsersComponent),
      },
      {
        path: 'users/invite',
        canActivate: [authGuard, superadminGuard],
        loadComponent: () => import('./features/users/user-invite.component').then((m) => m.UserInviteComponent),
      },
      {
        path: 'users/:id',
        canActivate: [authGuard, roleGuard('admin')],
        loadComponent: () => import('./features/users/user-edit.component').then((m) => m.UserEditComponent),
      },
      {
        path: 'organizations',
        canActivate: [authGuard, superadminGuard],
        data: { wide: true },
        loadComponent: () => import('./organizations/organizations.component').then((m) => m.OrganizationsComponent),
      },
      {
        path: 'organizations/new',
        canActivate: [authGuard, superadminGuard],
        loadComponent: () =>
          import('./features/organizations/organization-form.component').then((m) => m.OrganizationFormComponent),
      },
      {
        path: 'organizations/:id',
        canActivate: [authGuard, superadminGuard],
        loadComponent: () =>
          import('./features/organizations/organization-form.component').then((m) => m.OrganizationFormComponent),
      },
      {
        path: 'admin/logs',
        canActivate: [authGuard, superadminGuard],
        data: { wide: true },
        loadComponent: () => import('./features/admin/admin-logs.component').then((m) => m.AdminLogsComponent),
      },
    ],
  },

  { path: '**', redirectTo: '' },
];
